import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  KeyRound,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  SmartphoneNfc,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail, asDate, shopDateTime, SHOP_TZ, roleLabel } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const adminLike = (r) => r === "admin" || r === "it_admin";

export default function Users() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "staff",
    branch: "DANDENONG",
    temporary_password: false,
    counter_login: false,
  });
  const [issued, setIssued] = useState(null);
  const [lock, setLock] = useState({ allowed_ips: [], current_ip: "", locked: false });
  const [newIp, setNewIp] = useState("");
  const [sales, setSales] = useState({ month: "", by_staff: {} });
  const [editId, setEditId] = useState("");
  const [edit, setEdit] = useState({});
  const [activity, setActivity] = useState(null);

  const loadLock = () =>
    api
      .get("/settings/counter-login")
      .then(({ data }) => setLock(data))
      .catch(() => {});

  const saveLock = async (allowed_ips) => {
    try {
      const { data } = await api.put("/settings/counter-login", { allowed_ips });
      setLock({ ...data, current_ip: data.current_ip || lock.current_ip });
      setNewIp("");
      toast.success(
        allowed_ips.length
          ? "Counter logins are now limited to your shop networks"
          : "Location lock removed — every account is asked for an emailed code again"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const load = () =>
    api
      .get("/users")
      .then(({ data }) => setUsers(data))
      .catch(() => setUsers([]));

  const loadSales = () =>
    api
      .get("/users/activity-summary")
      .then(({ data }) => setSales(data))
      .catch(() => {});

  const act = async (fn, okMessage) => {
    try {
      await fn();
      toast.success(okMessage);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveEdit = () =>
    act(async () => {
      await api.put(`/users/${editId}`, edit);
      setEditId("");
    }, "Account updated");

  const openActivity = async (u) => {
    setActivity({ name: u.name, rows: null });
    try {
      const { data } = await api.get(`/users/${u.id}/activity`);
      setActivity(data);
    } catch (err) {
      setActivity(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load();
    loadLock();
    loadSales();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users", form);
      if (data.temporary_password) {
        setIssued({ email: data.email, password: data.temporary_password });
        toast.success("Account created — write down the one-time password");
      } else {
        setIssued(null);
        toast.success(
          data.invite_emailed
            ? "Invite emailed — they choose their own password"
            : "Account created, but the invite email could not be sent"
        );
      }
      setForm({ name: "", email: "", role: "staff", branch: "DANDENONG",
                temporary_password: false, counter_login: false });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const resetPassword = async (u, temporary) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {
        temporary_password: temporary,
      });
      if (data.temporary_password) {
        setIssued({ email: u.email, password: data.temporary_password });
        toast.success("One-time password issued");
      } else {
        toast.success(`Reset link sent to ${u.email}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="fade-up space-y-7" data-testid="users-page">
      <div>
        <h1 className="text-3xl font-bold sm:text-4xl">Staff Accounts</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Give each salesperson their own login, then manage them here — edit details,
          suspend a login, sign someone out of every device or open their activity.
        </p>
      </div>

      <form
        onSubmit={add}
        className="grid grid-cols-1 items-end gap-4 border border-border p-5 sm:grid-cols-6"
        data-testid="add-user-form"
      >
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Name</Label>
          <Input
            className="h-10 rounded-none"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="user-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Email</Label>
          <Input
            type="email"
            className="h-10 rounded-none"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="user-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Sign-in</Label>
          <Select
            value={form.temporary_password ? "otp" : "invite"}
            onValueChange={(v) => setForm({ ...form, temporary_password: v === "otp" })}
          >
            <SelectTrigger className="h-10 rounded-none" data-testid="user-signin-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              <SelectItem value="invite">Email them an invite</SelectItem>
              <SelectItem value="otp">One-time password</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="h-10 rounded-none" data-testid="user-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              {can("users:manage-itadmin") && (
                <SelectItem value="it_admin">IT Administrator</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Store</Label>
          <Select
            value={adminLike(form.role) ? "ALL" : form.branch}
            onValueChange={(v) => setForm({ ...form, branch: v })}
            disabled={adminLike(form.role)}
          >
            <SelectTrigger className="h-10 rounded-none" data-testid="user-branch-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              <SelectItem value="DANDENONG">Dandenong</SelectItem>
              <SelectItem value="TRUGANINA">Truganina</SelectItem>
              {adminLike(form.role) && <SelectItem value="ALL">Both stores</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Second step</Label>
          <div
            className="flex h-10 items-center border border-border bg-[#FBF8F2] px-3 text-xs text-muted-foreground"
            data-testid="user-secondstep-note"
          >
            {adminLike(form.role)
              ? "Emailed 6-digit code (always)"
              : "Password only — staff don't get a code"}
          </div>
        </div>
        <Button
          type="submit"
          className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="create-user-button"
        >
          <UserPlus className="mr-1.5 h-4 w-4" /> Create
        </Button>
      </form>

      <div className="border border-border p-5" data-testid="counter-login-lock">
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 text-[#B38F4E]" />
          <div>
            <p className="text-sm font-semibold">Shop network addresses (optional)</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Staff already sign in with just their email and password — no emailed code.
              Only owner/admin accounts get the 6-digit code. This location list is kept
              for reference only and is no longer required for staff sign-in.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {lock.allowed_ips.map((ip) => (
            <span
              key={ip}
              className="tabular flex items-center gap-1.5 border border-border bg-[#FBF8F2] px-2.5 py-1 text-xs"
              data-testid={`allowed-ip-${ip}`}
            >
              {ip}
              <button
                onClick={() => saveLock(lock.allowed_ips.filter((x) => x !== ip))}
                aria-label={`Remove ${ip}`}
                data-testid={`remove-ip-${ip}`}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {!lock.allowed_ips.length && (
            <span className="text-xs text-muted-foreground" data-testid="no-locations">
              No addresses saved — not required, staff sign in with a password only.
            </span>
          )}
        </div>
        <div className="mt-4 border border-border bg-[#FBF8F2] px-3 py-2" data-testid="detected-ip">
          <p className="text-xs text-muted-foreground">
            Address this computer is being seen as:{" "}
            <span className="tabular font-semibold text-foreground" data-testid="detected-ip-value">
              {lock.current_ip || "—"}
            </span>
          </p>
          {lock.current_ip && !lock.ip_trusted && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="detected-ip-warning">
              Read this from a computer inside the store — the address depends on the
              shop’s internet connection, not on this app.
            </p>
          )}
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            className="h-10 max-w-[240px] rounded-none"
            placeholder="e.g. 203.0.113.24"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            data-testid="new-ip-input"
          />
          <Button
            variant="outline"
            className="h-10 rounded-none"
            disabled={!newIp.trim()}
            onClick={() => saveLock([...lock.allowed_ips, newIp.trim()])}
            data-testid="add-ip-button"
          >
            Add this address
          </Button>
          {lock.current_ip && !lock.allowed_ips.includes(lock.current_ip) && (
            <Button
              variant="outline"
              className="h-10 rounded-none"
              onClick={() => saveLock([...lock.allowed_ips, lock.current_ip])}
              data-testid="add-current-ip-button"
            >
              Use this computer’s address ({lock.current_ip})
            </Button>
          )}
        </div>
      </div>

      {issued && (
        <div className="border-2 border-[#C5A059] bg-[#FBF8F2] p-4" data-testid="issued-password">
          <p className="text-sm font-semibold">
            One-time password for {issued.email}
          </p>
          <p className="tabular mt-2 text-2xl font-bold tracking-widest" data-testid="issued-password-value">
            {issued.password}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Write this down now — it is shown once and never stored in readable form.
            They will be asked to choose their own password when they first sign in.
          </p>
          <button
            onClick={() => setIssued(null)}
            className="mt-3 text-xs underline"
            data-testid="dismiss-issued-password"
          >
            Done, hide it
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[860px] text-sm" data-testid="users-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">Name</th>
              <th className="px-3 py-2.5 text-left font-semibold">Email</th>
              <th className="px-3 py-2.5 text-left font-semibold">Role</th>
              <th className="px-3 py-2.5 text-left font-semibold">Store</th>
              <th className="px-3 py-2.5 text-left font-semibold">Sign-in</th>
              <th className="px-3 py-2.5 text-left font-semibold">Last signed in</th>
              <th className="px-3 py-2.5 text-right font-semibold">
                Sales {sales.month ? `— ${sales.month}` : ""}
              </th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) =>
              editId === u.id ? (
                <tr key={u.id} className="bg-[#FBF8F2]" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2">
                    <Input
                      className="h-9 rounded-none"
                      value={edit.name}
                      onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                      data-testid={`edit-name-${u.email}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="email"
                      className="h-9 rounded-none"
                      value={edit.email}
                      onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                      data-testid={`edit-email-${u.email}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={edit.role}
                      onValueChange={(v) =>
                        setEdit({ ...edit, role: v, branch: adminLike(v) ? "" : edit.branch })
                      }
                    >
                      <SelectTrigger className="h-9 rounded-none" data-testid={`edit-role-${u.email}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-white">
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {can("users:manage-itadmin") && (
                          <SelectItem value="it_admin">IT Administrator</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2" colSpan={3}>
                    <Select
                      value={adminLike(edit.role) ? "ALL" : edit.branch || "DANDENONG"}
                      onValueChange={(v) => setEdit({ ...edit, branch: v })}
                      disabled={adminLike(edit.role)}
                    >
                      <SelectTrigger className="h-9 w-40 rounded-none" data-testid={`edit-branch-${u.email}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-white">
                        <SelectItem value="DANDENONG">Dandenong</SelectItem>
                        <SelectItem value="TRUGANINA">Truganina</SelectItem>
                        {adminLike(edit.role) && <SelectItem value="ALL">Both stores</SelectItem>}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2" colSpan={2}>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        onClick={saveEdit}
                        className="h-9 rounded-none bg-[#C5A059] text-[#0A0A0A] hover:bg-[#B38F4E]"
                        data-testid={`save-user-${u.email}`}
                      >
                        Save
                      </Button>
                      <button
                        onClick={() => setEditId("")}
                        className="text-xs uppercase tracking-wider text-muted-foreground hover:text-[#0A0A0A]"
                        data-testid={`cancel-user-${u.email}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr
                  key={u.id}
                  className={`hover:bg-[#FBF8F2] ${u.suspended ? "opacity-60" : ""}`}
                  data-testid={`user-row-${u.email}`}
                >
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => openActivity(u)}
                      className="text-left font-medium hover:text-[#B38F4E] hover:underline"
                      data-testid={`activity-${u.email}`}
                      title="See everything this person has done"
                    >
                      {u.name}
                    </button>
                    {u.suspended && (
                      <span
                        className="ml-2 border border-destructive px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-destructive"
                        data-testid={`suspended-badge-${u.email}`}
                      >
                        Suspended
                      </span>
                    )}
                    {!u.suspended && u.must_change_password && !u.last_login_at && (
                      <span
                        className="ml-2 border border-[#C5A059] px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#8A6C36]"
                        data-testid={`not-activated-${u.email}`}
                      >
                        Not activated
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">{u.email}</td>
                  <td className="px-3 py-2.5 text-xs uppercase tracking-wider">{roleLabel(u.role)}</td>
                  <td className="px-3 py-2.5 text-xs uppercase tracking-wider">
                    {adminLike(u.role) ? "Both stores" : u.branch || "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    {adminLike(u.role) ? (
                      <span className="text-xs text-muted-foreground">Code always</span>
                    ) : (
                      <span
                        className="text-xs text-muted-foreground"
                        data-testid={`signin-${u.email}`}
                      >
                        Password only
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground" data-testid={`last-login-${u.email}`}>
                    {u.last_login_at ? (
                      <>
                        {asDate(u.last_login_at).toLocaleString("en-AU", {
                          timeZone: SHOP_TZ,
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {u.last_login_ip && <div className="tabular">{u.last_login_ip}</div>}
                      </>
                    ) : (
                      "Never"
                    )}
                  </td>
                  <td className="tabular px-3 py-2.5 text-right" data-testid={`sales-${u.email}`}>
                    {sales.by_staff?.[u.name] ? (
                      <>
                        ${sales.by_staff[u.name].total.toLocaleString("en-AU", {
                          minimumFractionDigits: 2,
                        })}
                        <div className="text-[11px] text-muted-foreground">
                          {sales.by_staff[u.name].count} invoice
                          {sales.by_staff[u.name].count === 1 ? "" : "s"}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditId(u.id);
                          setEdit({
                            name: u.name || "",
                            email: u.email,
                            role: u.role,
                            branch: u.branch || "DANDENONG",
                          });
                        }}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`edit-user-${u.email}`}
                        title="Edit name, email, role or store"
                        aria-label="Edit account"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => resetPassword(u, true)}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`otp-reset-${u.email}`}
                        title="Set a new password now and show it on screen"
                        aria-label="Reset password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => resetPassword(u, false)}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`email-reset-${u.email}`}
                        title="Email them a link so they choose their own password"
                        aria-label="Email reset link"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          act(
                            () => api.post(`/users/${u.id}/reset-second-step`),
                            `${u.name} can sign in fresh — code cleared`
                          )
                        }
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`reset-second-step-${u.email}`}
                        title="Clear a stuck 6-digit code and forget trusted devices"
                        aria-label="Reset second step"
                      >
                        <SmartphoneNfc className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          act(
                            () => api.post(`/users/${u.id}/sign-out-everywhere`),
                            `${u.name} signed out of every device`
                          )
                        }
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`sign-out-all-${u.email}`}
                        title="Sign this person out of every device"
                        aria-label="Sign out everywhere"
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          act(
                            () =>
                              api.patch(`/users/${u.id}/suspend`, { suspended: !u.suspended }),
                            u.suspended
                              ? `${u.name} can sign in again`
                              : `${u.name} is suspended and signed out`
                          )
                        }
                        className={`border px-2 py-1 text-[11px] uppercase tracking-wider transition-colors duration-150 ${
                          u.suspended
                            ? "border-[#B38F4E] text-[#8A6C36] hover:bg-[#B38F4E] hover:text-white"
                            : "border-border text-muted-foreground hover:border-destructive hover:text-destructive"
                        }`}
                        data-testid={`suspend-${u.email}`}
                        title={u.suspended ? "Let them sign in again" : "Freeze this login"}
                      >
                        {u.suspended ? "Reactivate" : "Suspend"}
                      </button>
                      <button
                        onClick={() => remove(u)}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                        data-testid={`delete-user-${u.email}`}
                        aria-label="Delete user"
                        title="Delete permanently — suspend instead if they may return"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {activity && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setActivity(null)}
        >
          <div
            className="h-full w-full max-w-lg overflow-y-auto border-l border-border bg-white p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="activity-panel"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Activity
                </p>
                <h2 className="text-xl font-bold">{activity.name}</h2>
              </div>
              <button
                onClick={() => setActivity(null)}
                className="text-muted-foreground hover:text-[#0A0A0A]"
                data-testid="close-activity"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {activity.rows === null && (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
              {activity.rows?.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="activity-empty">
                  Nothing recorded for this person yet.
                </p>
              )}
              {activity.rows?.map((r) => (
                <div
                  key={r.id}
                  className="border-l-2 border-[#C5A059] pl-3"
                  data-testid={`activity-row-${r.id}`}
                >
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <Activity className="h-3 w-3" />
                    {r.action.replace(/[:_-]/g, " ")}
                    {!r.allowed && (
                      <span className="text-destructive"> · blocked</span>
                    )}
                  </p>
                  <p className="text-sm">{r.summary || "—"}</p>
                  <p className="tabular text-[11px] text-muted-foreground">
                    {shopDateTime(r.at)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
